/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebase';
import {
    loadQuizProgress,
    markQuizCompleted,
    saveSlideProgress,
    saveQuizAnswer,
    saveQuizProgress
} from '../utils/quizUtilityNew';
import CertificateGenerator from './CertificateGenerator';
import './PeerSupportCourse.css';

const FlightSchoolPeerSupportCourse = () => {
    const [user, loading] = useAuthState(auth);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [progressLoaded, setProgressLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showCertificate, setShowCertificate] = useState(false);

    // Authentication modal state
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [authForm, setAuthForm] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [resetEmail, setResetEmail] = useState('');
    const [authError, setAuthError] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(false);

    // Course content slides
    const slides = [
        {
            id: 0,
            title: "Peer Support in Flight Schools",
            content: (
                <div className="text-center slide-content">
                    <img src="/assets/logo.png" alt="PilotHelper Logo" style={{maxWidth: '90%', marginBottom: '2rem', display: 'block', margin: '0 auto 2rem auto'}} />
                    <h1 className="display-4 mb-4">Peer Support in Flight Schools</h1>
                    <div className="mt-4">
                        <h4 className="mb-3">Goals:</h4>
                        <ul className="list-unstyled text-start mx-auto" style={{maxWidth: '600px'}}>
                            <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i>Build a culture of support between flight instructors and student pilots</li>
                            <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i>Highlight peer support skills associated with student pilot support systems</li>
                            <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i>Discuss the flight instructor-student pilot relationship and roles</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 1,
            title: "Why Flight Instructor Support Matters",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Why Flight Instructor Support Matters</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <ul className="list-unstyled">
                                <li className="mb-3">
                                    <i className="fas fa-star text-warning me-2"></i>
                                    <strong>Flight instructors are often the first and most influential mentors</strong> in a student pilot's aviation journey
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-shield-alt text-primary me-2"></i>
                                    Flight instructor support directly impacts <strong>student confidence, safety, and decision-making</strong>
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-heart text-danger me-2"></i>
                                    Supportive flight instructors help reduce common stressors of student pilots, such as <strong>performance anxiety</strong>, especially during:
                                    <ul className="mt-2">
                                        <li>Stage checks</li>
                                        <li>Checkrides</li>
                                        <li>High-pressure training milestones</li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <div className="col-md-4">
                            <img src="/assets/flightschool2.jpg" alt="Flight Training" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 2,
            title: "The Role of Flight Instructors in Student Pilot Support",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">The Role of Flight Instructors in Student Pilot Support</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <h5 className="mb-3">Positive flight instructor relationships encourage:</h5>
                            <ul className="list-unstyled mb-4">
                                <li className="mb-2"><i className="fas fa-comments text-info me-2"></i>Open communication</li>
                                <li className="mb-2"><i className="fas fa-handshake text-success me-2"></i>Trust-based learning environment</li>
                                <li className="mb-2"><i className="fas fa-users text-primary me-2"></i>Supportive conversations around challenges</li>
                            </ul>

                            <h5 className="mb-3">When student pilots feel supported, they are better equipped to:</h5>
                            <ul className="list-unstyled mb-4">
                                <li className="mb-2"><i className="fas fa-check text-success me-2"></i>Stay engaged and receptive to instruction</li>
                                <li className="mb-2"><i className="fas fa-check text-success me-2"></i>Learn from mistakes with confidence and resilience</li>
                                <li className="mb-2"><i className="fas fa-check text-success me-2"></i>Communicate questions, concerns and challenges</li>
                            </ul>

                            <div className="alert alert-info">
                                <strong>Bottom Line:</strong> Meaningful flight instructor support promotes safer pilots and a healthier aviation community.
                            </div>
                        </div>
                        <div className="col-md-4">
                            <img src="/assets/flightschool3.jpg" alt="Learning Environment" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 3,
            title: "The Flight Instructor-Student Pilot Relationship",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">The Flight Instructor-Student Pilot Relationship</h2>
                    <div className="row justify-content-center">
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-4 border rounded h-100" style={{backgroundColor: 'rgba(32, 201, 151, 0.1)'}}>
                                <div className="mb-3 text-success">
                                    <i className="fas fa-graduation-cap fa-3x"></i>
                                </div>
                                <h5>Learning & Growth</h5>
                                <p>Flight instructors shape learning, confidence, and professional identity</p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-4 border rounded h-100" style={{backgroundColor: 'rgba(32, 201, 151, 0.1)'}}>
                                <div className="mb-3 text-info">
                                    <i className="fas fa-handshake fa-3x"></i>
                                </div>
                                <h5>Trust & Communication</h5>
                                <p>Trust and communication are critical for student success</p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-4 border rounded h-100" style={{backgroundColor: 'rgba(32, 201, 151, 0.1)'}}>
                                <div className="mb-3 text-primary">
                                    <i className="fas fa-users fa-3x"></i>
                                </div>
                                <h5>Peer Support</h5>
                                <p>Peer support strengthens instructional effectiveness</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 4,
            title: "Core Peer Support Skills",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Core Peer Support Skills</h2>
                    <div className="row">
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-3 border rounded h-100 bg-dark">
                                <div className="mb-3 text-warning">
                                    <i className="fas fa-ear-listen fa-3x"></i>
                                </div>
                                <h5 className="text-white">Active Listening</h5>
                                <p className="text-light">Active listening without interruption</p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-3 border rounded h-100 bg-dark">
                                <div className="mb-3 text-info">
                                    <i className="fas fa-heart fa-3x"></i>
                                </div>
                                <h5 className="text-white">Empathy</h5>
                                <p className="text-light">Demonstrating empathy and understanding</p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-3 border rounded h-100 bg-dark">
                                <div className="mb-3 text-success">
                                    <i className="fas fa-comments fa-3x"></i>
                                </div>
                                <h5 className="text-white">Open Dialogue</h5>
                                <p className="text-light">Encouraging open dialogue</p>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="text-center p-3 border rounded h-100 bg-dark">
                                <div className="mb-3 text-primary">
                                    <i className="fas fa-balance-scale fa-3x"></i>
                                </div>
                                <h5 className="text-white">Non-judgmental Approach</h5>
                                <p className="text-light">Maintaining a non-judgmental approach</p>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="text-center p-3 border rounded h-100 bg-dark">
                                <div className="mb-3 text-warning">
                                    <i className="fas fa-hands-helping fa-3x"></i>
                                </div>
                                <h5 className="text-white">Normalizing Challenges</h5>
                                <p className="text-light">Normalizing challenges in training</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 5,
            title: "What Peer Support Is (and Is Not)",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">What Peer Support Is (and Is Not)</h2>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="card border-success mb-4">
                                <div className="card-header bg-success text-white">
                                    <h5 className="mb-0"><i className="fas fa-check-circle me-2"></i>Peer Support IS:</h5>
                                </div>
                                <div className="card-body">
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-3">
                                            <i className="fas fa-check text-success me-2"></i>
                                            Supportive listening, shared experience, encouragement
                                        </li>
                                        <li className="mb-0">
                                            <i className="fas fa-check text-success me-2"></i>
                                            Peer-level, respectful, and confidential
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card border-danger mb-4">
                                <div className="card-header bg-danger text-white">
                                    <h5 className="mb-0"><i className="fas fa-times-circle me-2"></i>Peer Support IS NOT:</h5>
                                </div>
                                <div className="card-body">
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-3">
                                            <i className="fas fa-times text-danger me-2"></i>
                                            Diagnosis, treatment, or disciplinary action
                                        </li>
                                        <li className="mb-0">
                                            <i className="fas fa-times text-danger me-2"></i>
                                            Meant to replace professional mental health services
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 6,
            title: "Flight Instructors Create a Culture of Support",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Flight Instructors Create a Culture of Support</h2>
                    <div className="row justify-content-center">
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-4 border rounded h-100" style={{backgroundColor: '#20c997', color: 'white'}}>
                                <div className="mb-3">
                                    <i className="fas fa-handshake fa-3x"></i>
                                </div>
                                <h5>Trust & Communication</h5>
                                <p>Fosters trust and respectful communication</p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-4 border rounded h-100" style={{backgroundColor: '#20c997', color: 'white'}}>
                                <div className="mb-3">
                                    <i className="fas fa-clipboard-check fa-3x"></i>
                                </div>
                                <h5>Early Check-ins</h5>
                                <p>Encourage early check-ins with students</p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="text-center p-4 border rounded h-100" style={{backgroundColor: '#20c997', color: 'white'}}>
                                <div className="mb-3">
                                    <i className="fas fa-chart-line fa-3x"></i>
                                </div>
                                <h5>Better Outcomes</h5>
                                <p>Improve safety, learning outcomes, and morale</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    const quizQuestions = [
        {
            id: 1,
            question: "Which is a core peer support skill?",
            options: [
                "Giving advice immediately",
                "Active listening",
                "Changing the subject quickly"
            ],
            correct: 1,
            explanation: "Active listening without interruption is a core peer support skill that helps build trust and understanding."
        },
        {
            id: 2,
            question: "Which is a common stressor for student pilots?",
            options: [
                "Performance anxiety",
                "Lack of responsibility",
                "Excessive free time"
            ],
            correct: 0,
            explanation: "Performance anxiety is a common stressor for student pilots, especially during stage checks, checkrides, and high-pressure training milestones."
        },
        {
            id: 3,
            question: "Normalizing challenges in training helps student pilots because it:",
            options: [
                "Removes the need for effort",
                "Automatically fixes training difficulties",
                "Helps individuals feel less alone and more supported"
            ],
            correct: 2,
            explanation: "Normalizing challenges helps student pilots feel less alone and more supported, which builds resilience and confidence."
        },
        {
            id: 4,
            question: "Which factor strongly influences the flight instructor-student pilot relationship?",
            options: [
                "Aircraft type",
                "Trust and communication",
                "Airport location"
            ],
            correct: 1,
            explanation: "Trust and communication are critical factors that strongly influence the flight instructor-student pilot relationship and student success."
        },
        {
            id: 5,
            question: "Why is peer support essential to flight school culture?",
            options: [
                "It eliminates stress",
                "It improves trust, safety, and resilience",
                "It removes all stress"
            ],
            correct: 1,
            explanation: "Peer support improves trust, safety, and resilience within flight schools, creating a healthier learning environment."
        }
    ];

    const totalSlides = slides.length + quizQuestions.length;
    const isQuizSection = currentSlide >= slides.length;
    const currentQuizIndex = currentSlide - slides.length;

    // Authentication functions
    const closeAuthModal = () => {
        setShowAuthModal(false);
        setIsSignUp(false);
        setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
        setAuthError('');
        setIsAuthLoading(false);
    };

    const closePasswordResetModal = () => {
        setShowPasswordResetModal(false);
        setResetEmail('');
        setResetError('');
        setResetSuccess(false);
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setIsAuthLoading(true);
        setResetError('');

        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(true);
        } catch (error) {
            let errorMessage = 'An error occurred';

            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email address';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                default:
                    errorMessage = error.message;
            }

            setResetError(errorMessage);
        }

        setIsAuthLoading(false);
    };

    const handleFormChange = (e) => {
        setAuthForm({
            ...authForm,
            [e.target.name]: e.target.value
        });
        setAuthError('');
    };

    const handleGoogleSignIn = () => {
        signInWithPopup(auth, googleProvider)
            .then(() => {
                closeAuthModal();
            })
            .catch((err) => {
                console.error('Google sign in error:', err);
                setAuthError('Failed to sign in with Google. Please try again.');
            });
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setIsAuthLoading(true);
        setAuthError('');

        try {
            if (isSignUp) {
                if (!authForm.name.trim()) {
                    setAuthError('Name is required');
                    setIsAuthLoading(false);
                    return;
                }
                if (authForm.password !== authForm.confirmPassword) {
                    setAuthError('Passwords do not match');
                    setIsAuthLoading(false);
                    return;
                }
                if (authForm.password.length < 6) {
                    setAuthError('Password must be at least 6 characters');
                    setIsAuthLoading(false);
                    return;
                }

                const result = await createUserWithEmailAndPassword(auth, authForm.email, authForm.password);

                await updateProfile(result.user, {
                    displayName: authForm.name.trim()
                });
            } else {
                await signInWithEmailAndPassword(auth, authForm.email, authForm.password);
            }

            closeAuthModal();

            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (error) {
            let errorMessage = 'An error occurred';

            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'This email is already registered';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password';
                    break;
                case 'auth/invalid-credential':
                    errorMessage = 'Invalid email or password';
                    break;
                default:
                    errorMessage = error.message;
            }

            setAuthError(errorMessage);
        }

        setIsAuthLoading(false);
    };

    // Load user's progress when they sign in
    useEffect(() => {
        const loadUserProgress = async () => {
            if (user && !progressLoaded) {
                try {
                    const progress = await loadQuizProgress(user.uid, 'flight-school');
                    setCurrentSlide(progress.currentSlide);
                    setQuizAnswers(progress.quizAnswers);
                    setShowResults(progress.showResults);
                    setProgressLoaded(true);

                    const userDocRef = doc(db, 'quizProgress', user.uid);
                    await setDoc(userDocRef, {
                        userName: user.displayName || 'Unknown User',
                        userEmail: user.email || '',
                        lastUpdated: new Date().toISOString()
                    }, { merge: true });

                } catch (error) {
                    console.error('Failed to load progress:', error);
                    setProgressLoaded(true);
                }
            }
        };

        if (!loading && user) {
            loadUserProgress();
        }
    }, [user, loading, progressLoaded]);

    // Save progress when slide changes
    useEffect(() => {
        if (user && progressLoaded && currentSlide >= 0) {
            setSaving(true);
            const timeoutId = setTimeout(async () => {
                try {
                    await saveSlideProgress(user.uid, 'flight-school', currentSlide);
                } catch (error) {
                    console.error('Failed to save progress:', error);
                } finally {
                    setSaving(false);
                }
            }, 500);

            return () => {
                clearTimeout(timeoutId);
                setSaving(false);
            };
        }
    }, [currentSlide, user, progressLoaded]);

    const handleNext = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handlePrev = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentSlide]);

    const handleQuizAnswer = async (questionId, answerIndex) => {
        const question = quizQuestions.find(q => q.id === questionId);
        const isCorrect = answerIndex === question.correct;

        const newQuizAnswers = {
            ...quizAnswers,
            [questionId]: {
                selected: answerIndex,
                correct: isCorrect
            }
        };

        setQuizAnswers(newQuizAnswers);

        if (user) {
            try {
                await saveQuizAnswer(user.uid, 'flight-school', questionId, answerIndex, isCorrect);
            } catch (error) {
                console.error('Failed to save quiz answer:', error);
            }
        }

        const answeredQuestions = Object.keys(newQuizAnswers).length;

        if (answeredQuestions === quizQuestions.length) {
            setShowResults(true);

            const correctAnswers = Object.values(newQuizAnswers).filter(answer => answer.correct).length;
            if (user && correctAnswers === quizQuestions.length) {
                try {
                    await markQuizCompleted(user.uid, 'flight-school', {
                        currentSlide,
                        quizAnswers: newQuizAnswers,
                        showResults: true
                    });
                } catch (error) {
                    console.error('Failed to mark quiz as completed:', error);
                }
            }
        }
    };

    const resetQuiz = async () => {
        setQuizAnswers({});
        setShowResults(false);
        setCurrentSlide(slides.length);

        if (user) {
            try {
                await saveQuizProgress(user.uid, 'flight-school', {
                    currentSlide: slides.length,
                    quizAnswers: {},
                    showResults: false,
                    completedAt: null
                }, user);
            } catch (error) {
                console.error('Failed to reset quiz in database:', error);
            }
        }
    };

    const getCorrectAnswers = () => {
        return Object.values(quizAnswers).filter(answer => answer.correct).length;
    };

    if (loading) {
        return (
        <div className='peer-support-course-container'>
            <div className="container">
                <div className="loading-container">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="loading-text">Loading...</p>
                </div>
            </div>
        </div>
        );
    }

    if (!user) {
        return (
            <>
            <div className='auth-screen-container'>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6 text-center">
                            <img src="/assets/logo.png" alt="PilotHelper Logo" style={{maxWidth: '300px', marginBottom: '2rem', display: 'block', margin: '0 auto 2rem auto'}} />
                            <h2 className="mb-4">Sign in Required</h2>
                            <p className="lead mb-4">
                                Please sign in to access the Flight School Peer Support Course.
                                Your progress will be saved and you can resume from any device.
                            </p>

                            <div className="d-grid gap-2">
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={() => setShowAuthModal(true)}
                                >
                                    <i className="fas fa-user me-2"></i>
                                    Sign In / Sign Up
                                </button>
                            </div>
                        </div>
                    </div>

                    {showAuthModal && (
                        <div
                            className="modal show d-block"
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                            onClick={closeAuthModal}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="fs-auth-modal-title"
                        >
                            <div
                                className="modal-dialog modal-dialog-centered"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="modal-content">
                                    <div className="modal-header border-0">
                                        <h5 id="fs-auth-modal-title" className="modal-title">
                                            {isSignUp ? 'Create Account' : 'Sign In'}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={closeAuthModal}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        {/* Google Sign In Button */}
                                        <button
                                            onClick={handleGoogleSignIn}
                                            className="btn btn-outline-dark w-100 mb-3 d-flex align-items-center justify-content-center"
                                            disabled={isAuthLoading}
                                        >
                                            <img
                                                src="https://developers.google.com/identity/images/g-logo.png"
                                                alt="Google"
                                                width="20"
                                                className="me-2"
                                            />
                                            Continue with Google
                                        </button>

                                        <div className="text-center mb-3">
                                            <span className="text-muted">or</span>
                                        </div>

                                        {authError && (
                                            <div className="alert alert-danger" role="alert">
                                                {authError}
                                            </div>
                                        )}

                                        <form onSubmit={handleEmailAuth}>
                                            {isSignUp && (
                                                <div className="mb-3">
                                                    <label htmlFor="name" className="form-label">Full Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="name"
                                                        name="name"
                                                        value={authForm.name}
                                                        onChange={handleFormChange}
                                                        required
                                                        disabled={isAuthLoading}
                                                        placeholder="Enter your full name"
                                                    />
                                                </div>
                                            )}
                                            <div className="mb-3">
                                                <label htmlFor="email" className="form-label">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    name="email"
                                                    value={authForm.email}
                                                    onChange={handleFormChange}
                                                    required
                                                    disabled={isAuthLoading}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="password" className="form-label">Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="password"
                                                    name="password"
                                                    value={authForm.password}
                                                    onChange={handleFormChange}
                                                    required
                                                    disabled={isAuthLoading}
                                                    minLength="6"
                                                />
                                            </div>
                                            {isSignUp && (
                                                <div className="mb-3">
                                                    <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                                                    <input
                                                        type="password"
                                                        className="form-control"
                                                        id="confirmPassword"
                                                        name="confirmPassword"
                                                        value={authForm.confirmPassword}
                                                        onChange={handleFormChange}
                                                        required
                                                        disabled={isAuthLoading}
                                                        minLength="6"
                                                    />
                                                </div>
                                            )}
                                            <button
                                                type="submit"
                                                className="btn btn-primary w-100 mb-3"
                                                disabled={isAuthLoading}
                                            >
                                                {isAuthLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        {isSignUp ? 'Creating Account...' : 'Signing In...'}
                                                    </>
                                                ) : (
                                                    isSignUp ? 'Create Account' : 'Sign In'
                                                )}
                                            </button>
                                        </form>

                                        {!isSignUp && (
                                            <div className="text-center mb-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none"
                                                    onClick={() => {
                                                        closeAuthModal();
                                                        setShowPasswordResetModal(true);
                                                    }}
                                                    style={{fontSize: '0.9rem'}}
                                                >
                                                    Forgot your password?
                                                </button>
                                            </div>
                                        )}

                                        <div className="text-center">
                                            <span className="text-muted">
                                                {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={() => {
                                                    setIsSignUp(!isSignUp);
                                                    setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
                                                    setAuthError('');
                                                }}
                                                disabled={isAuthLoading}
                                            >
                                                {isSignUp ? 'Sign In' : 'Sign Up'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

                {showPasswordResetModal && (
                    <div
                        className="modal show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        onClick={closePasswordResetModal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="fs-reset-modal-title"
                    >
                        <div
                            className="modal-dialog modal-dialog-centered"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 id="fs-reset-modal-title" className="modal-title">
                                        Reset Password
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closePasswordResetModal}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {!resetSuccess ? (
                                        <>
                                            <p className="text-muted mb-4">
                                                Enter your email address and we'll send you a link to reset your password.
                                            </p>

                                            {resetError && (
                                                <div className="alert alert-danger" role="alert">
                                                    {resetError}
                                                </div>
                                            )}

                                            <form onSubmit={handlePasswordReset}>
                                                <div className="mb-3">
                                                    <label htmlFor="resetEmail" className="form-label">Email Address</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        id="resetEmail"
                                                        value={resetEmail}
                                                        onChange={(e) => setResetEmail(e.target.value)}
                                                        required
                                                        disabled={isAuthLoading}
                                                        placeholder="Enter your email address"
                                                    />
                                                </div>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary w-100 mb-3"
                                                    disabled={isAuthLoading}
                                                >
                                                    {isAuthLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Sending Reset Email...
                                                        </>
                                                    ) : (
                                                        'Send Reset Email'
                                                    )}
                                                </button>
                                            </form>

                                            <div className="text-center">
                                                <span className="text-muted">
                                                    Remember your password?
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 ms-1"
                                                    onClick={() => {
                                                        closePasswordResetModal();
                                                        setShowAuthModal(true);
                                                    }}
                                                    disabled={isAuthLoading}
                                                >
                                                    Sign In
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center">
                                            <div className="text-success mb-3">
                                                <i className="fas fa-check-circle fa-3x"></i>
                                            </div>
                                            <h5 className="mb-3">Reset Email Sent!</h5>
                                            <p className="text-muted mb-4">
                                                We've sent a password reset link to <strong>{resetEmail}</strong>.
                                                Check your email and follow the instructions to reset your password.
                                            </p>
                                            <p className="text-muted small mb-4">
                                            Don't see the email? <strong>Check your spam folder or try again with a different email address.</strong>
                                            </p>
                                            <button
                                                className="btn btn-primary"
                                                onClick={closePasswordResetModal}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    const renderSlide = () => {
        if (isQuizSection) {
            const question = quizQuestions[currentQuizIndex];
            const userAnswer = quizAnswers[question.id];

            return (
                <div>
                    <h2 className="mb-4">Question {question.id} of {quizQuestions.length}</h2>
                    <div className="card quiz-card">
                        <div className="card-body">
                            <h4 className="card-title mb-4">{question.question}</h4>
                            <div className="d-grid gap-2">
                                {question.options.map((option, index) => {
                                    let className = "btn btn-outline-primary text-start";
                                    let disabled = false;

                                    if (userAnswer) {
                                        disabled = true;
                                        if (index === question.correct) {
                                            className = "btn btn-success text-start";
                                        } else if (index === userAnswer.selected && !userAnswer.correct) {
                                            className = "btn btn-danger text-start";
                                        } else {
                                            className = "btn btn-outline-secondary text-start";
                                        }
                                    }

                                    return (
                                        <button
                                            key={index}
                                            className={className}
                                            onClick={() => handleQuizAnswer(question.id, index)}
                                            disabled={disabled}
                                        >
                                            {String.fromCharCode(97 + index)}) {option}
                                        </button>
                                    );
                                })}
                            </div>

                            {userAnswer && (
                                <div className={`alert quiz-explanation mt-4 ${userAnswer.correct ? 'alert-success' : 'alert-warning'}`}>
                                    <strong>{userAnswer.correct ? 'Correct!' : 'Incorrect.'}</strong>
                                    <p className="mb-0 mt-2">{question.explanation}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        } else {
            return slides[currentSlide].content;
        }
    };

    const renderQuizResults = () => {
        const correctAnswers = getCorrectAnswers();
        const passed = correctAnswers === quizQuestions.length;
        const allQuestionsAnswered = Object.keys(quizAnswers).length === quizQuestions.length;

        return (
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h2 className="mb-0">Quiz Results</h2>
                </div>
                <div className="card-body">
                    <div className="text-center mb-4">
                        <h3>Your Score: {correctAnswers}/{quizQuestions.length} ({Math.round((correctAnswers / quizQuestions.length) * 100)}%)</h3>
                        {passed ? (
                            <div className="alert alert-success">
                                <h4>Congratulations!</h4>
                                <p>You've successfully completed the Flight School Peer Support Course!</p>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={() => setShowCertificate(true)}
                                >
                                    Get Your Certificate
                                </button>
                            </div>
                        ) : (
                            <div className="alert alert-warning">
                                <h4>Almost there!</h4>
                                <p>You need to get all questions correct to pass. Please review and try again.</p>
                                <button
                                    className="btn btn-warning"
                                    onClick={resetQuiz}
                                >
                                    Retake Quiz
                                </button>
                            </div>
                        )}
                    </div>

                    {allQuestionsAnswered && (
                        <div className="quiz-review">
                            <h4>Review Your Answers:</h4>
                            {quizQuestions.map((question, index) => {
                                const userAnswer = quizAnswers[question.id];
                                return (
                                    <div key={question.id} className="question-card">
                                        <div className="question-header">
                                            Question {index + 1} of {quizQuestions.length}
                                        </div>
                                        <div className="question-body">
                                            <div className="question-text">
                                                {question.question}
                                            </div>
                                            <div className="answer-options">
                                                {question.options.map((option, optionIndex) => {
                                                    let badgeClass = "bg-secondary";

                                                    if (userAnswer && userAnswer.selected === optionIndex) {
                                                        badgeClass = userAnswer.correct ? "bg-success" : "bg-danger";
                                                    } else if (optionIndex === question.correct) {
                                                        badgeClass = "bg-success";
                                                    }

                                                    return (
                                                        <div key={optionIndex} className="answer-option">
                                                            <span className={`badge ${badgeClass} flex-shrink-0`}>
                                                                {String.fromCharCode(65 + optionIndex)}
                                                            </span>
                                                            <span>{option}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="explanation-section">
                                                <small>
                                                    <strong>Explanation:</strong> {question.explanation}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className='peer-support-course-container'>
                <div className="container">
                    <div className='course-content-wrapper'>
                        <div className="row justify-content-center">
                            <div className="col-lg-10">
                                {/* User Info */}
                                <div className='user-info-section'>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <small className="text-muted">
                                                <strong>{user.displayName || user.email?.split('@')[0] || 'User'}</strong> ({user.email})
                                            </small>
                                        </div>
                                        <div>
                                            {saving ? (
                                                <small className="text-warning">
                                                    <i className="fas fa-spinner fa-spin me-1"></i>
                                                    Saving...
                                                </small>
                                            ) : (
                                                <small className="text-success">
                                                    <i className="fas fa-check me-1"></i>
                                                    Progress saved
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="course-progress-section">
                                    <div className="progress">
                                        <div
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{width: `${((currentSlide + 1) / totalSlides) * 100}%`}}
                                        >
                                            {Math.round(((currentSlide + 1) / totalSlides) * 100)}%
                                        </div>
                                    </div>
                                    <small className="text-muted progress-info">
                                        Slide {currentSlide + 1} of {totalSlides}
                                        {isQuizSection && " (Quiz Section)"}
                                    </small>
                                </div>

                                {/* Course Content */}
                                {showResults ? renderQuizResults() : renderSlide()}

                                {/* Navigation */}
                                {!showResults && (
                                    <div className='course-navigation'>
                                        <div className="d-flex justify-content-between mt-4">
                                            <button
                                                className="btn btn-secondary"
                                                onClick={handlePrev}
                                                disabled={currentSlide === 0}
                                            >
                                                <i className="fas fa-chevron-left me-2"></i>
                                                Previous
                                            </button>

                                            <button
                                                className="btn btn-primary"
                                                onClick={handleNext}
                                                disabled={currentSlide === totalSlides - 1 || (isQuizSection && !quizAnswers[quizQuestions[currentQuizIndex].id])}
                                            >
                                                Next
                                                <i className="fas fa-chevron-right ms-2"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Certificate Generator Modal */}
            {showCertificate && (
                <CertificateGenerator
                    user={user}
                    courseType="flight-school"
                    onClose={() => setShowCertificate(false)}
                />
            )}
        </>
    );
};

export default FlightSchoolPeerSupportCourse;
